import React, {
  Activity, Suspense, memo, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore,
} from 'react';
import { Route, Routes, UNSAFE_LocationContext, UNSAFE_RouteContext, useLocation } from 'react-router-dom';
import AgendaPage from '../pages/app/AgendaPage';
import TasksPage from '../pages/app/TasksPage';
import ProjectsPage from '../pages/app/ProjectsPage';
import ChatLinkPage from '../pages/app/ChatLinkPage';
import { RouteChunkFallback } from '../components/skeletons/PageSkeletons';
import { lazyPage } from '../lib/lazyPage';

export const PlannerPage = lazyPage(() => import('../pages/app/PlannerPage'));
export const BotsPage = lazyPage(() => import('../pages/app/BotsPage'));
export const ChatsPage = lazyPage(() => import('../pages/app/ChatsPage'));
export const SchedulesPage = lazyPage(() => import('../pages/app/SchedulesPage'));

/*
 * The phone's bottom tabs, like a native tab bar: each tab's screen stays drawn (hidden,
 * in <Activity>) while another tab is open, so going back to it shows it at once, as it
 * was, scrolled where it was. Tabs not opened yet are drawn ahead when the phone is idle.
 *
 * A hidden tab keeps the address it was drawn with, in its own location and route
 * context (<Routes> reads the router's location even when given one), so taps
 * elsewhere do not draw it again. The same screens as the /app routes in App.js,
 * with full paths.
 *
 * The tab bar's Tasks shows two kept screens, the list and the Planner (its calendar), so
 * switching between them is instant; Chats are the history, its chats and managing the bots (an
 * older link to a bot's page lands there too, pages/app/ChatLinkPage.jsx).
 */
const TABS = [
  { id: 'home', root: '/app/agenda', match: (p) => p === '/app' || p === '/app/agenda' },
  { id: 'tasks', root: '/app/tasks', match: (p) => p === '/app/tasks' },
  { id: 'planner', root: '/app/planner', match: (p) => p === '/app/planner' },
  { id: 'chats', root: '/app/chats', match: (p) => p === '/app/chats' || p.startsWith('/app/chats/') || p === '/app/bots' || p.startsWith('/app/bots/') },
  { id: 'projects', root: '/app/projects', match: (p) => p === '/app/projects' },
];
const SCREEN_ROUTES = (
  <>
    <Route path="/app" element={<AgendaPage />} />
    <Route path="/app/agenda" element={<AgendaPage />} />
    <Route path="/app/planner" element={<PlannerPage />} />
    <Route path="/app/tasks" element={<TasksPage />} />
    <Route path="/app/projects" element={<ProjectsPage />} />
    <Route path="/app/bots" element={<BotsPage />} />
    <Route path="/app/bots/scheduled" element={<SchedulesPage />} />
    <Route path="/app/bots/:botId" element={<ChatLinkPage />} />
    <Route path="/app/bots/:botId/chats" element={<ChatLinkPage />} />
    <Route path="/app/chats/:chatId?" element={<ChatsPage />} />
  </>
);
const ROOT_ROUTE = { outlet: null, matches: [], isDataRoute: false };  // the router's own default
const FALLBACK = <RouteChunkFallback />;

export const tabOf = (pathname) => TABS.find((tab) => tab.match(pathname))?.id || null;

// Where each tab was last, and which was shown last of some (read by MobileBottomNav: Tasks
// goes back to the list or the Planner, whichever the person left; Chats to where it was).
const kept = {};
export const keptPath = (id) => (kept[id] ? `${kept[id].pathname}${kept[id].search}` : null);
const shownAt = {};
let shownTick = 0;
export const lastShown = (ids) => ids.reduce((best, id) => ((shownAt[id] || 0) > (shownAt[best] || 0) ? id : best), ids[0]);

// A tap on the tab bar, before the router has the new address: a tab that is drawn
// already at that address is shown by an urgent update (an external store's updates are
// never deferred) right after the lit tab is painted, instead of after the router's
// transition. The router then brings the rest (title, address) right behind it.
let pressed = null;
const pressListeners = new Set();
const setPressed = (next) => { pressed = next; pressListeners.forEach((fn) => fn()); };
const subscribePressed = (fn) => { pressListeners.add(fn); return () => pressListeners.delete(fn); };
const getPressed = () => pressed;
const samePath = (a, b) => (a === '/app' ? '/app/agenda' : a) === (b === '/app' ? '/app/agenda' : b);
let routedAddress = null;  // the router's address at the last commit
const pressTab = (to) => {
  const [pathname, search = ''] = to.split('?');
  const query = search ? `?${search}` : '';
  if (routedAddress && samePath(routedAddress.pathname, pathname) && routedAddress.search === query) return;  // the router was faster
  const tab = tabOf(pathname);
  const at = tab && kept[tab];
  if (at && samePath(at.pathname, pathname) && at.search === query) setPressed(tab);
};
// Called on the tap: the lit tab is painted first (the next frame), then the screen
// is shown, so a heavy one (the Planner's calendar) never holds back the tap's response.
export const showTabSoon = (to) => {
  window.requestAnimationFrame(() => window.setTimeout(() => pressTab(to), 0));
};

const sameState = (a, b) => {
  try { return JSON.stringify(a ?? null) === JSON.stringify(b ?? null); } catch { return a === b; }
};
const sameAddress = (a, b) => !!a && a.pathname === b.pathname && a.search === b.search
  && a.hash === b.hash && sameState(a.state, b.state);

const Screen = memo(function Screen({ location }) {
  const here = useMemo(() => ({ location, navigationType: 'POP' }), [location]);
  return (
    <UNSAFE_LocationContext.Provider value={here}>
      <UNSAFE_RouteContext.Provider value={ROOT_ROUTE}>
        <Suspense fallback={FALLBACK}>
          <Routes location={location}>{SCREEN_ROUTES}</Routes>
        </Suspense>
      </UNSAFE_RouteContext.Provider>
    </UNSAFE_LocationContext.Provider>
  );
});

const whenIdle = (fn) => (typeof window.requestIdleCallback === 'function'
  ? window.requestIdleCallback(fn, { timeout: 3000 })
  : window.setTimeout(fn, 400));
const cancelIdle = (id) => (typeof window.cancelIdleCallback === 'function'
  ? window.cancelIdleCallback(id)
  : window.clearTimeout(id));

export default function TabScreens({ scrollRef, children }) {
  const location = useLocation();
  const pressedTab = useSyncExternalStore(subscribePressed, getPressed, getPressed);
  const routed = tabOf(location.pathname);
  const active = pressedTab || routed;
  // A tab keeps the location it was drawn with while its address stays the same, so
  // coming back to it through the tab bar (a new history entry) does not draw it again.
  const activeLocation = pressedTab && pressedTab !== routed
    ? kept[pressedTab]
    : (active && sameAddress(kept[active], location) ? kept[active] : location);
  const [, drewAhead] = useState(0);

  useLayoutEffect(() => {
    if (active) kept[active] = activeLocation;
  });
  useLayoutEffect(() => {
    if (active) { shownTick += 1; shownAt[active] = shownTick; }
  }, [active]);
  // The router has the tapped address: from here on it decides again.
  useLayoutEffect(() => {
    routedAddress = location;
    if (pressed) setPressed(null);
  }, [location]);

  // Each place keeps its own scroll position; moving deeper inside a tab starts at the top.
  // Paths are compared per tab: the router catching up with a tap shown already is no move.
  const scrollOf = useRef({});
  const pathOf = useRef({});
  const activeRef = useRef(active);
  const shownPath = active ? activeLocation.pathname : location.pathname;
  useLayoutEffect(() => {
    const box = scrollRef.current;
    const place = active || 'other';
    const tabChanged = activeRef.current !== active;
    const pathChanged = pathOf.current[place] !== undefined && pathOf.current[place] !== shownPath;
    activeRef.current = active;
    pathOf.current[place] = shownPath;
    if (!box) return;
    if (tabChanged) box.scrollTop = active ? (scrollOf.current[active] || 0) : 0;
    else if (pathChanged) box.scrollTop = 0;
  }, [active, shownPath, scrollRef]);
  useEffect(() => {
    const box = scrollRef.current;
    if (!box) return undefined;
    const onScroll = () => { if (activeRef.current) scrollOf.current[activeRef.current] = box.scrollTop; };
    box.addEventListener('scroll', onScroll, { passive: true });
    return () => box.removeEventListener('scroll', onScroll);
  }, [scrollRef]);

  // The tabs not opened yet, drawn one at a time while the phone is idle (without their
  // effects: they load nothing until they are shown).
  useEffect(() => {
    let id = null;
    const next = () => {
      const tab = TABS.find((t) => t.root && !kept[t.id]);
      if (!tab) return;
      kept[tab.id] = { pathname: tab.root, search: '', hash: '', state: null, key: `ahead-${tab.id}` };
      drewAhead((n) => n + 1);
      id = whenIdle(next);
    };
    id = whenIdle(next);
    return () => cancelIdle(id);
  }, []);

  return (
    <>
      {TABS.map(({ id }) => {
        const tabLocation = id === active ? activeLocation : kept[id];
        if (!tabLocation) return null;
        return (
          <Activity key={id} mode={id === active ? 'visible' : 'hidden'}>
            <Screen location={tabLocation} />
          </Activity>
        );
      })}
      {!active && children}
    </>
  );
}
